import { useEffect, useState } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

export default function Grievance() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, message: `[GRIEVANCE] ${form.message}` });
      toast.success("Grievance registered. Our team will respond within 3 business days.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit");
    } finally { setLoading(false); }
  };

  return (
    <MarketingLayout>
      <section className="page-shell px-6 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="grievance-page">
        <div className="lg:col-span-5 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Grievance Redressal</div>
            <h1 className="font-display text-5xl lg:text-6xl mt-3 leading-[1.05]">We take your concerns seriously.</h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-md">Finzpe follows the RBI Fair Practice Code. If you have a concern about our services, please reach out — we&apos;ll respond within three business days.</p>
          </div>
          <Card className="p-6 rounded-2xl">
            <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-2 flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Escalation Matrix</div>
            <ol className="text-sm space-y-3 list-decimal list-inside">
              <li><span className="font-medium">Level 1</span> — Write to <a className="text-primary" href="mailto:support@finzpe.com">support@finzpe.com</a> (T+3 business days).</li>
              <li><span className="font-medium">Level 2</span> — Escalate to the Grievance Officer at <a className="text-primary" href="mailto:grievance@finzpe.com">grievance@finzpe.com</a> if unresolved.</li>
              <li><span className="font-medium">Level 3</span> — RBI Integrated Ombudsman: <a className="text-primary" href="https://cms.rbi.org.in" target="_blank" rel="noreferrer">cms.rbi.org.in</a>.</li>
            </ol>
            <div className="mt-4 text-xs text-muted-foreground">Nodal Officer: Sonal Verma · <a className="underline" href="mailto:nodal@finzpe.com">nodal@finzpe.com</a> · +91 87000 59442</div>
          </Card>
        </div>
        <Card className="lg:col-span-7 p-8 rounded-3xl">
          <form onSubmit={submit} className="space-y-4" data-testid="grievance-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label htmlFor="gname">Name</Label><Input id="gname" required value={form.name} onChange={set("name")} data-testid="grievance-name" /></div>
              <div><Label htmlFor="gemail">Email</Label><Input id="gemail" type="email" required value={form.email} onChange={set("email")} data-testid="grievance-email" /></div>
            </div>
            <div><Label htmlFor="gphone">Phone</Label><Input id="gphone" value={form.phone} onChange={set("phone")} /></div>
            <div><Label htmlFor="gmsg">Describe your grievance</Label><Textarea id="gmsg" rows={7} required value={form.message} onChange={set("message")} data-testid="grievance-message" /></div>
            <Button type="submit" disabled={loading} className="rounded-full brand-gradient text-white" data-testid="grievance-submit">{loading ? "Submitting..." : "Submit grievance"}</Button>
          </form>
        </Card>
      </section>
    </MarketingLayout>
  );
}
